// src/components/common/TimePickerModal.tsx

import React, { useState, useEffect, useRef } from 'react';
import TimeWheelPicker from './TimeWheelPicker';
import { generateSmartQuickTimes, QuickTimeOption } from '../../utils/timePickerHelpers';
import PickerShell from './PickerShell';

interface TimePickerModalProps {
  /** Modal 標題（保留 prop 相容，但不顯示） */
  title?: string;

  /** 當前選中的時間值 (HH:MM 格式，24小時制) */
  currentValue?: string;

  /** 選擇回調 - 返回 HH:MM 格式（24小時制） */
  onSelect: (value: string) => void;

  /** 關閉回調 */
  onClose: () => void;

  /** 返回上一步回調（返回日期選擇），有傳才顯示 Back 按鈕 */
  onBack?: () => void;

  /** 時間間隔（分鐘）- 預設 15 分鐘 */
  interval?: 5 | 10 | 15 | 30 | 60;

  /** 最小時間限制 (HH:MM 格式) */
  minTime?: string;

  /** 最大時間限制 (HH:MM 格式) */
  maxTime?: string;

  /** 使用 24 小時制顯示（預設 true） */
  use24Hours?: boolean;

  /** 選擇器模式：wheel（iOS滾輪）或 list（滾動清單） */
  pickerMode?: 'wheel' | 'list';

  /** 是否顯示 Clear 按鈕（預設 false） */
  showClearButton?: boolean;
}

/**
 * 時間選擇器 Modal
 * 提供快捷時間選項和完整時間列表
 * 用於 TimeBox 的開始/結束時間選擇
 *
 * 多步驟流程（日期→時間）時，傳入 onBack 即顯示返回按鈕
 */
const TimePickerModal: React.FC<TimePickerModalProps> = ({
  currentValue,
  onSelect,
  onClose,
  onBack,
  interval = 15,
  minTime,
  maxTime,
  use24Hours = true,
  pickerMode = 'list',
  showClearButton = false,
}) => {
  const [selectedTime, setSelectedTime] = useState<string | undefined>(currentValue);
  const timeListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedTime(currentValue);
  }, [currentValue]);

  useEffect(() => {
    if (selectedTime && timeListRef.current) {
      const selectedButton = timeListRef.current.querySelector(
        `[data-time="${selectedTime}"]`
      ) as HTMLElement;
      if (selectedButton) {
        setTimeout(() => {
          selectedButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
  }, [selectedTime]);

  const formatTime24To12 = (time24: string): string => {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  const formatTimeDisplay = (time24: string): string => {
    return use24Hours ? time24 : formatTime24To12(time24);
  };

  const generateTimeList = (): string[] => {
    const times: string[] = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += interval) {
        times.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
      }
    }
    return times;
  };

  const getQuickTimeOptions = (): QuickTimeOption[] => {
    const now = new Date();
    const baseTime = currentValue || `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    return generateSmartQuickTimes(baseTime, interval, formatTimeDisplay);
  };

  const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const isTimeInRange = (time: string): boolean => {
    if (!minTime && !maxTime) return true;
    const timeMinutes = timeToMinutes(time);
    if (minTime && timeMinutes < timeToMinutes(minTime)) return false;
    if (maxTime && timeMinutes > timeToMinutes(maxTime)) return false;
    return true;
  };

  const handleQuickOptionClick = (time: string) => {
    if (!isTimeInRange(time)) return;
    onSelect(time);
    onClose();
  };

  const handleTimeClick = (time: string) => {
    if (!isTimeInRange(time)) return;
    onSelect(time);
    onClose();
  };

  const handleClear = () => {
    onSelect('');
    onClose();
  };

  const handleConfirm = () => {
    if (selectedTime) onSelect(selectedTime);
    onClose();
  };

  const quickOptions = getQuickTimeOptions();
  const timeList = generateTimeList();

  return (
    <PickerShell onClose={onClose} onBack={onBack}>
      <div className="max-h-[calc(90vh-56px)] flex flex-col overflow-hidden">
        {/* Quick Time Options */}
        <div className="px-4 pt-3 pb-2 border-b border-gray-100">
          <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-hide">
            {quickOptions.map((option) => {
              const isSelected = selectedTime === option.value;
              const isDisabled = !isTimeInRange(option.value);
              return (
                <button
                  key={option.value}
                  onClick={() => handleQuickOptionClick(option.value)}
                  disabled={isDisabled}
                  className={`
                    px-4 py-2 rounded-xl text-sm font-medium
                    whitespace-nowrap flex-shrink-0 min-w-[80px]
                    transition-all border-2 flex items-center justify-center
                    ${isSelected
                      ? 'bg-sky-50 text-sky-700 border-sky-200'
                      : isDisabled
                      ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-sky-200 hover:bg-sky-50'
                    }
                  `}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Time Picker */}
        {pickerMode === 'wheel' ? (
          <TimeWheelPicker
            value={selectedTime || currentValue || '09:00'}
            onChange={(time) => setSelectedTime(time)}
            onConfirm={handleConfirm}
            interval={interval}
            minTime={minTime}
            maxTime={maxTime}
            use24Hours={use24Hours}
            className="py-4"
          />
        ) : (
          <div
            ref={timeListRef}
            className="flex-1 overflow-y-auto px-4 py-3"
            style={{ maxHeight: '300px' }}
          >
            <div className="space-y-1">
              {timeList.map((time) => {
                const isSelected = selectedTime === time;
                const isDisabled = !isTimeInRange(time);
                return (
                  <button
                    key={time}
                    data-time={time}
                    onClick={() => handleTimeClick(time)}
                    disabled={isDisabled}
                    className={`
                      w-full px-4 py-2.5 rounded-xl text-sm font-medium
                      transition-all border-2 text-left
                      ${isSelected
                        ? 'bg-sky-50 text-sky-700 border-sky-200'
                        : isDisabled
                        ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                        : 'bg-gray-50 text-gray-700 border-transparent hover:border-sky-200 hover:bg-sky-50'
                      }
                    `}
                  >
                    {formatTimeDisplay(time)}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer Actions */}
        {(pickerMode === 'wheel' || showClearButton) && (
          <div className="px-4 py-3 border-t border-gray-100">
            {pickerMode === 'wheel' ? (
              <button
                onClick={handleConfirm}
                className="w-full px-4 py-2.5 rounded-xl text-sm font-medium
                  text-white bg-sky-500 hover:bg-sky-600 transition-colors"
              >
                Done
              </button>
            ) : (
              showClearButton && (
                <button
                  onClick={handleClear}
                  className="w-full px-4 py-2.5 rounded-xl text-sm font-medium
                    text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  Clear
                </button>
              )
            )}
          </div>
        )}
      </div>
    </PickerShell>
  );
};

export default TimePickerModal;
